import React, { useState } from 'react';
import { Box, Typography, CircularProgress, IconButton, Link } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpIcon from '@mui/icons-material/Help';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import { useNavigate } from 'react-router-dom';
import workAnalyzerService from '../../services/workAnalyzerService';
import { InspectionRegistryStub } from '../../models/inspectionEvaluationIssue';

const MAX_ADDRESS_LENGTH = 25;

interface InspectionRegistryListDisplayProps {
  registry: InspectionRegistryStub[];
  currentPendingId: string | null;
  setCurrentPendingId: React.Dispatch<React.SetStateAction<string | null>>;
  setLoadingMessage: React.Dispatch<React.SetStateAction<string>>;
  fetchRegistry: () => void;
}

const InspectionRegistryListDisplay: React.FC<InspectionRegistryListDisplayProps> = ({
  registry,
  currentPendingId,
  setCurrentPendingId,
  setLoadingMessage,
  fetchRegistry,
}) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const navigate = useNavigate();

  const handleViewDocument = async (inspectionId: string) => {
    try {
      const documentUrl = await workAnalyzerService.getInspectionDocument(inspectionId);
      if (documentUrl) {
        window.open(documentUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error("Failed to fetch inspection document:", error);
    }
  };

  const handleRetry = async (inspectionId: string) => {
    setIsRetrying(true);
    setCurrentPendingId(inspectionId);
    setLoadingMessage("Re-analyzing inspection data...");

    try {
      await workAnalyzerService.createNewInspectionEvaluation(inspectionId);
      setCurrentPendingId(null);
      fetchRegistry();
    } catch (error) {
      console.error("Retry failed:", error);
      setLoadingMessage("Retry failed. Please try again.");
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      {[...registry]
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .map((inspection) => (
          <Box
            key={inspection.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid #ddd',
              padding: 1,
              flexWrap: 'nowrap',
            }}
          >
            {/* Address Column */}
            <Box sx={{ flexGrow: 1, overflowWrap: 'anywhere', maxWidth: '60%' }}>
              <Link
                component="button"
                variant="body1"
                onClick={() => navigate(`/inspection/${inspection.id}`)}
                sx={{ cursor: 'pointer', color: 'primary.main' }}
              >
                {inspection.address.length > MAX_ADDRESS_LENGTH
                  ? `${inspection.address.slice(0, MAX_ADDRESS_LENGTH)}...`
                  : inspection.address}
              </Link>
            </Box>

            {/* Date Column */}
            <Typography variant="body2" sx={{ whiteSpace: 'nowrap', marginLeft: '8px' }}>
              {new Date(inspection.date).toLocaleDateString()} {new Date(inspection.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>

            {/* Actions and Status Column */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {currentPendingId === inspection.id ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  {(inspection.status === "SUCCESS" || inspection.status === "COMPLETED") && <CheckCircleIcon color="success" />}
                  {(inspection.status === "ERROR" || inspection.status === "FAILED" || inspection.status === "SYNTHESIS_FAILED") && <ErrorIcon color="error" />}
                  {inspection.status === "COMPLETED_WITH_ERRORS" && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleIcon sx={{ color: 'orange' }} />
                      <Typography variant="caption" sx={{ ml: 0.5, color: 'orange' }}>
                        Partial
                      </Typography>
                    </Box>
                  )}
                  {(inspection.status === "PENDING" || 
                    inspection.status === "PROCESSING" || 
                    inspection.status === "PAGES_GENERATED" || 
                    inspection.status === "ANALYZING" || 
                    inspection.status === "SYNTHESIZING") && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CircularProgress size={16} sx={{ mr: 0.5 }} />
                      <Typography variant="caption" sx={{ color: 'primary.main' }}>
                        {inspection.status === "PROCESSING" && "Processing"}
                        {inspection.status === "PAGES_GENERATED" && "Pages Ready"}
                        {inspection.status === "ANALYZING" && "Analyzing"}
                        {inspection.status === "SYNTHESIZING" && "Finalizing"}
                        {inspection.status === "PENDING" && "Pending"}
                      </Typography>
                    </Box>
                  )}
                  {inspection.status === "PARTIAL" && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CheckCircleIcon sx={{ color: 'orange' }} />
                      <Typography variant="caption" sx={{ ml: 0.5, color: 'orange' }}>
                        Partial
                      </Typography>
                    </Box>
                  )}
                </>
              )}
              <IconButton
                size="small"
                onClick={() => handleViewDocument(inspection.id)}
                color="primary"
                aria-label="View Document"
              >
               <DescriptionIcon />

              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleRetry(inspection.id)}
                disabled={isRetrying || currentPendingId === inspection.id}
                aria-label="Retry"
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
        ))}
    </Box>
  );
};

export default InspectionRegistryListDisplay;
